'use client';

import React, { useCallback, useEffect, useMemo } from 'react';
import { isEmpty } from 'lodash';
import { useDropzone } from 'react-dropzone';
import { useController, useFormContext } from 'react-hook-form';
import { FormFeedback } from 'reactstrap';

interface DropzoneFileUploaderProps {
  noDrag?: boolean;
  maxSize?: number;
  boundary?: React.ReactNode;
  maxFiles?: number | null;
  multiple?: boolean;
  acceptedFileTypes?: string[];
  name: string;
  customHandleChange?: () => void;
  disabled?: boolean;
  hideErrorMessage?: boolean;
  enableSetErrorMessageInHookForm?: boolean;
}

interface FileRejectionError {
  code: string;
  message: string;
}

const DropzoneFileUploader = (props: DropzoneFileUploaderProps) => {
  const {
    noDrag = false,
    maxSize = 104857600,
    boundary,
    maxFiles = null,
    multiple = true,
    acceptedFileTypes,
    name: droppedField,
    customHandleChange,
    disabled = false,
    hideErrorMessage = false,
    enableSetErrorMessageInHookForm = false,
  } = props;
  const { setError } = useFormContext();
  const { field: { onChange } } = useController({ name: droppedField });

  const onDropAction = useCallback((droppedFiles: File[]) => {
    if (!isEmpty(droppedFiles)) {
      onChange(droppedFiles);
      if (customHandleChange) {
        customHandleChange();
      }
    }
  }, [onChange, customHandleChange]);

  const generateAcceptedFileTypes = (): Record<string, string[]> => {
    let acceptedFiles: Record<string, string[]> = {};

    const imageFileTypes = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg'];
    const documentFileTypes = ['pdf', 'doc', 'docx', 'ppt', 'pptx', 'xls', 'xlsx', 'csv', 'txt'];

    acceptedFileTypes?.forEach((extension) => {
      if (imageFileTypes.includes(extension)) {
        acceptedFiles = { ...acceptedFiles, [`image/${extension}`]: [] };
      } else if (documentFileTypes.includes(extension)) {
        acceptedFiles = { ...acceptedFiles, [`text/${extension}`]: [`.${extension}`] };
      }
    });
    return acceptedFiles;
  };

  const {
    getRootProps,
    getInputProps,
    fileRejections,
  } = useDropzone({
    noDrag,
    maxSize,
    maxFiles: maxFiles ?? undefined,
    multiple,
    onDrop: onDropAction,
    accept: generateAcceptedFileTypes(),
    disabled,
  });

  const allErrors = useMemo<FileRejectionError[]>(() => [], []);
  fileRejections.map((errors) => (
    errors.errors.map((error) => allErrors.push(error))
  ));

  const uniqueErrors = allErrors.filter((error, index, array) => (
    array.findIndex((item) => item?.code === error?.code) === index));

  const displayErrors = (errors: FileRejectionError): string => {
    if (errors.code === 'file-invalid-type') {
      const fileTypes = acceptedFileTypes?.join(', ')?.toUpperCase();
      return `Only ${fileTypes} files are accepted!`;
    }
    if (errors.code === 'file-too-large') {
      return `File should be smaller than ${maxSize ? maxSize / 1024 / 1024 : 10}MB!`;
    }
    if (errors.code === 'too-many-files') {
      return `You can only select ${maxFiles} File!`;
    }
    return errors.message;
  };

  const fileRejectionItems = uniqueErrors.map((errors) => (
    <div key={errors?.code} className="jd-card-upload--errors">
      <ul>
        <li>
          <FormFeedback>{displayErrors(errors)}</FormFeedback>
        </li>
      </ul>
    </div>
  ));

  useEffect(() => {
    if (!isEmpty(fileRejections) && enableSetErrorMessageInHookForm) {
      const errorTypes = allErrors.reduce<Record<string, string>>((acc, error) => {
        acc[error?.code] = error?.message;
        return acc;
      }, {});
      setError(droppedField, { type: 'custom', message: allErrors[0]?.message, types: errorTypes });
    }
  }, [setError, fileRejections, droppedField, enableSetErrorMessageInHookForm, allErrors]);

  return (
    <React.Fragment>
      <div {...getRootProps({ className: 'dropzone ' })}>
        <input {...getInputProps()} />
        {boundary}
      </div>
      {!hideErrorMessage && fileRejectionItems ? fileRejectionItems : ''}
    </React.Fragment>
  );
};

export default DropzoneFileUploader;
